import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { CONTACT } from "@/lib/api";

const WhatsAppIcon = ({ className = "h-7 w-7" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const QUICK_CHIPS = ["Loans & Funding", "Wealth Portfolio", "Interior Design", "Digital Marketing"];

export default function WhatsAppWidget() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const close = (e) => {
      if (open && e.target.closest?.("[data-whatsapp-root]") === null) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const send = () => {
    const text = msg.trim() || "Hello Evolve Elite, I'd like to know more about your services.";
    window.open(`${CONTACT.whatsapp}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
    setMsg("");
  };

  return (
    <div className="fixed bottom-5 right-5 z-[70] sm:bottom-6 sm:right-6" data-whatsapp-root data-testid="whatsapp-chat-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.94 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mb-3 w-[calc(100vw-2.5rem)] max-w-[340px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_32px_64px_-16px_rgba(10,25,47,0.35)]"
          >
            <div className="flex items-center gap-3 bg-navy px-4 py-3.5">
              <LogoMark className="h-9 w-9" />
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-bold text-white">Evolve Elite Desk</p>
                <p className="flex items-center gap-1.5 text-[11px] text-emerald-300">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" /> Online — replies in minutes
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
                aria-label="Close chat"
                data-testid="whatsapp-chat-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 px-4 py-4">
              <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-[#F1F5F9] px-4 py-3 text-sm text-navy">
                Hello! How can Evolve Elite assist your financial, digital or design goals today?
              </div>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => setMsg(`Hi, I'd like to enquire about ${chip}.`)}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold text-navy transition hover:border-gold hover:bg-gold/10"
                    data-testid={`whatsapp-chip-${chip.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 border-t border-slate-100 px-3 py-3">
              <input
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your message…"
                className="min-w-0 flex-1 rounded-full border border-slate-200 bg-[#F8FAFC] px-4 py-2.5 text-sm outline-none focus:border-gold"
                data-testid="whatsapp-chat-input"
              />
              <button
                onClick={send}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white transition hover:brightness-110 active:scale-95"
                aria-label="Send on WhatsApp"
                data-testid="whatsapp-send-message-btn"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((o) => !o)}
        className={`ml-auto flex h-14 w-14 items-center justify-center rounded-full text-white shadow-[0_18px_36px_-10px_rgba(37,211,102,0.6)] transition-transform duration-200 hover:scale-105 active:scale-95 ${
          open ? "bg-slate-800" : "pulse-ring bg-[#25D366]"
        }`}
        aria-label="Chat on WhatsApp"
        data-testid="whatsapp-chat-trigger"
      >
        {open ? <X className="h-6 w-6" /> : <WhatsAppIcon className="h-7 w-7" />}
      </button>
    </div>
  );
}
